import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSkillDevelopmentAsync,selectSkillDevelopmentData } from '../candidateSlice/SkillDevelopment/SkillDevelopmentSlice';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    useDisclosure,
    Spinner,
    Alert,
    AlertIcon,
    Box,
    SimpleGrid,
    Badge,
    Text,
    Heading,
    Flex,
    Icon,
    VStack,
    HStack,
    Container,
    Divider,
    Skeleton,
    Stack
} from '@chakra-ui/react';
import { FaDownload, FaCalendarAlt, FaFolderOpen, FaLightbulb } from 'react-icons/fa';
import { SKILL_DEVELOPMENT_VIEW } from '../../common/URLs';

// --- Helper Functions ---
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
        return 'Invalid Date';
    }
};

// --- Sub-components for better structure ---

const CategoryFilter = ({ categories, selectedCategory, onSelectCategory }) => (
    <Box mb={10}>
        <Heading as="h2" size="md" color="gray.700" mb={4}>
            Filter by Category
        </Heading>
        <Flex wrap="wrap" gap={3}>
            {categories.map((category) => (
                <Button
                    key={category}
                    size="sm"
                    onClick={() => onSelectCategory(category)}
                    colorScheme={selectedCategory === category ? 'blue' : 'gray'}
                    variant={selectedCategory === category ? 'solid' : 'outline'}
                    borderRadius="full"
                >
                    {category}
                </Button>
            ))}
        </Flex>
    </Box>
);

const SkillCard = ({ skill, onCardClick }) => (
    <Box
        bg="white"
        borderRadius="xl"
        boxShadow="md"
        overflow="hidden"
        transition="all 0.3s ease-in-out"
        _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
        cursor="pointer"
        onClick={() => onCardClick(skill)}
        borderTop="4px solid"
        borderColor="blue.400"
    >
        <VStack p={6} align="start" spacing={3}>
            <Badge colorScheme="blue" variant="subtle" fontSize="xs" py={1} px={2} borderRadius="md">
                {skill.job_department.title}
            </Badge>
            <Heading as="h3" size="md" fontWeight="600" color="gray.800" noOfLines={2}>
                {skill.title}
            </Heading>
            <Text color="gray.600" fontSize="sm" noOfLines={3}>
                {skill.description}
            </Text>
            <HStack color="gray.500" fontSize="xs" spacing={1} pt={2}>

                <Text>Posted: {formatDate(skill.created_at)}</Text>
            </HStack>
        </VStack>
    </Box>
);

const SkillDetailModal = ({ isOpen, onClose, skill }) => {
    if (!skill) return null;
    const openPdf = (url) => {
        if (!url) {
            console.error("PDF URL is missing.")
            return
        }
        const newTab = window.open(url, "_blank")
        if (!newTab) {
            console.error("Failed to open PDF. Please check your browser settings.")
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered motionPreset="slideInBottom">
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(3px)" />
            <ModalContent borderRadius="lg">
                <ModalHeader borderBottomWidth="1px" pb={4}>
                    <VStack align="start" spacing={2}>
                        <Badge colorScheme="blue" variant="solid">{skill.job_department?.title}</Badge>
                        <Heading as="h2" size="lg" color="gray.800">{skill.title}</Heading>
                    </VStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody py={6}>
                    <VStack spacing={4} align="start">
                        <Text fontSize="md" color="gray.700" whiteSpace="pre-wrap">
                            {skill.description}
                        </Text>
                        <Divider />
                        <HStack color="gray.500" fontSize="sm">
                            <Icon as={FaCalendarAlt} mr={1} />
                            <Text>Published on: {formatDate(skill.created_at)}</Text>
                        </HStack>
                    </VStack>
                </ModalBody>
                <ModalFooter bg="gray.50" borderTopRadius="lg">
                    {skill.attachment && (
                        <Button
                            as="a"

                            target="_blank"
                            rel="noopener noreferrer"
                            colorScheme="blue"
                            leftIcon={<FaDownload />}
                            mr={3}
                            onClick={() => openPdf(`${SKILL_DEVELOPMENT_VIEW}${skill.attachment}`)}
                        >
                            View Attachment
                        </Button>
                    )}
                    <Button variant="ghost" onClick={onClose}>
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const LoadingSkeletons = () => (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {Array.from({ length: 6 }).map((_, index) => (
            <Stack key={index} bg="white" p={6} borderRadius="xl" boxShadow="md" spacing={4}>
                <Skeleton height="20px" width="100px" borderRadius="md" />
                <Skeleton height="24px" width="80%" borderRadius="md" />
                <Skeleton height="60px" borderRadius="md" />
                <Skeleton height="16px" width="150px" borderRadius="md" />
            </Stack>
        ))}
    </SimpleGrid>
);

const EmptyState = () => (
    <Box textAlign="center" py={16} px={6} bg="white" borderRadius="xl" boxShadow="md">
        <Icon as={FaFolderOpen} color="blue.500" boxSize={16} mb={4} />
        <Heading as="h2" size="lg" fontWeight="600" color="gray.700" mb={2}>
            No Resources Found
        </Heading>
        <Text color="gray.500">
            There are currently no skill development items for the selected category.
        </Text>
    </Box>
);


// --- Main Component ---

const SkillsDevelopment = () => {
    const dispatch = useDispatch();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { data, loading, error } = useSelector((state) => state.skillDevelopment);

    const [selectedSkill, setSelectedSkill] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('All');

    useEffect(() => {
        dispatch(fetchSkillDevelopmentAsync());
    }, [dispatch]);

    const handleCardClick = (skill) => {
        setSelectedSkill(skill);
        onOpen();
    };

    const skills = useMemo(() => data?.data || [], [data]);

    const categories = useMemo(() => {
        if (!skills.length) return [];
        const uniqueCategories = new Set(skills.map(skill => skill.job_department.title));
        return ['All', ...Array.from(uniqueCategories)];
    }, [skills]);

    const filteredSkills = useMemo(() => {
        if (selectedCategory === 'All') return skills;
        return skills.filter(skill => skill.job_department.title === selectedCategory);
    }, [skills, selectedCategory]);

    const renderContent = () => {
        if (loading) {
            return <LoadingSkeletons />;
        }
        if (error) {
            return (
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    There was an error fetching resources: {error.message || 'Unknown error'}
                </Alert>
            );
        }
        if (filteredSkills.length > 0) {
            return (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                    {filteredSkills.map((skill) => (
                        <SkillCard key={skill.uuid} skill={skill} onCardClick={handleCardClick} />
                    ))}
                </SimpleGrid>
            );
        }
        return <EmptyState />;
    };


    return (
        <div>
            <div style={{ fontFamily: "Inter, sans-serif" }}>
                <div className="py-8 min-h-screen bg-mainBg sm:px-6 sm:py-4">
                    <div className="px-4 sm:px-0.5">

                        {/* Heading */}
                        <div className="mb-10">
                            <div className="flex items-center space-x-3 mb-1">

                                <h1 className="text-3xl font-bold text-gray-800">Skill Development</h1>
                            </div>
                            <p className="text-lg text-gray-600 max-w-3xl">
                                Explore these curated resources to enhance your professional skills and
                                accelerate your career growth.
                            </p>
                        </div>

                        {/* Category Filter */}
                        {categories.length > 1 && (
                            <CategoryFilter
                                categories={categories}
                                selectedCategory={selectedCategory}
                                onSelectCategory={setSelectedCategory}
                            />
                        )}

                        {/* Render Content */}
                        {renderContent()}

                    </div>
                </div>
            </div>

            {/* Skill Detail Modal */}
            <SkillDetailModal
                isOpen={isOpen}
                onClose={onClose}
                skill={selectedSkill}
            />
        </div>

    );
};

export default SkillsDevelopment;